import { getSessionState } from '@/stores/sessionStore';
import { performManualLogout } from '@/lib/axios';
import { performAutoLogin } from '@/lib/axios/auth-utils';
import { SESSION_URL } from '@/lib/axios/config';
import { toSessionData } from '@/hooks/useSession';

interface SafeSessionData {
  sub: string;
  name: string;
  email?: string;
  permissions: Permission[];
  organizations: Organization[];
}

interface Permission {
  id: string;
  roles: string[];
}

interface Organization {
  id: string;
  name: string;
}

interface UseSafeSessionReturn {
  isAuthenticated: boolean | null;
  sessionData: SafeSessionData | null;
  loading: boolean;
  error: string | null;
  logout: () => void;
}

type GlobalSessionBridge = {
  data: SafeSessionData | null;
  loading: boolean;
  error: string | null;
};

const SESSION_KEY = '__NSPORTAL_SESSION__';

const readGlobalSession = (): GlobalSessionBridge | undefined =>
  (globalThis as unknown as Record<string, GlobalSessionBridge | undefined>)[
    SESSION_KEY
  ];

const toSafeSessionData = (
  raw: ReturnType<typeof getSessionState>['data']
): SafeSessionData | null => {
  if (!raw) return null;
  return {
    sub: raw.sub,
    name: raw.name,
    email: raw.email,
    permissions: raw.permissions,
    organizations: raw.organizations,
  };
};

const fetchSession = async () => {
  const response = await fetch(SESSION_URL, { credentials: 'include' });

  if (response.status === 401) performAutoLogin();
  if (!response.ok) throw new Error('Não autenticado');

  return toSessionData(await response.json());
};

let _bootstrapPromise: Promise<void> | null = null;

// Usado quando o MFE roda standalone (sem nsportal principal no contexto).
// O top-level await abaixo faz o loadRemote bloquear até a sessão estar pronta,
// então os componentes já recebem dados reais na primeira renderização.
export const waitForSession = (): Promise<void> => {
  if (readGlobalSession() !== undefined) return Promise.resolve();
  if (_bootstrapPromise) return _bootstrapPromise;

  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('session timeout')), 5_000)
  );

  _bootstrapPromise = Promise.race([fetchSession(), timeout])
    .then(data => {
      (globalThis as Record<string, unknown>)[SESSION_KEY] = {
        data: toSafeSessionData(data),
        loading: false,
        error: null,
      };
    })
    .catch((err: unknown) => {
      (globalThis as Record<string, unknown>)[SESSION_KEY] = {
        data: null,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      };
    });

  return _bootstrapPromise;
};

await waitForSession();

export const useSession = (): UseSafeSessionReturn => {
  const store = getSessionState();
  const global = store.data === null ? readGlobalSession() : undefined;

  const sessionData =
    store.data === null
      ? (global?.data ?? null)
      : toSafeSessionData(store.data);
  const loading =
    store.data === null ? (global?.loading ?? false) : store.loading;
  const error = store.data === null ? (global?.error ?? null) : store.error;

  return {
    isAuthenticated: !!sessionData && !error,
    sessionData,
    loading,
    error,
    logout: performManualLogout,
  };
};
